import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import {
  OrdersFilterParams,
  UpdateOrderParams,
  UpdateOrderStatusParams,
  AdminCreateGuestOrderParams,
  AdminCreateRegisteredOrderParams,
  AddOrderItemParams,
  UpdateOrderItemParams,
  OrderNoteParams
} from '@/types/order'
import { BulkIdParams } from '@/types/general'
import { useOrdersStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'
import {
  createGuestOrder,
  createRegisteredOrder,
  updateOrder,
  addOrderItem,
  updateOrderItem,
  removeOrderItem,
  addOrderNote,
  updateOrderNote,
  deleteOrderNote,
  downloadOrderExport
} from '@/lib/api'

export const useOrders = (params: OrdersFilterParams) => {
  const fetchOrders = useOrdersStore(state => state.fetchOrders)

  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => fetchOrders(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useOrder = (id: string) => {
  const getOrder = useOrdersStore(state => state.getOrder)

  return useQuery({
    queryKey: ['order', id],
    queryFn: () => getOrder(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useUpdateOrder = (orderId: string) => {
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: (params: UpdateOrderParams) => updateOrder(orderId, params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      toast.success(t('updated_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_updating'))
    }
  })
}

export const useUpdateOrderStatus = () => {
  const updateOrderStatus = useOrdersStore(state => state.updateOrderStatus)
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateOrderStatusParams }) => updateOrderStatus(id, params),
    onSuccess: async (_, { id }) => {
      await queryClient.invalidateQueries({ queryKey: ['order', id] })
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      toast.success(t('updated_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_updating'))
    }
  })
}

export const useDeleteOrder = () => {
  const deleteOrder = useOrdersStore(state => state.deleteOrder)
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: (id: string) => deleteOrder(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(t('deleted_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_deleting'))
    }
  })
}

export const useBulkDeleteOrders = () => {
  const bulkDeleteOrders = useOrdersStore(state => state.bulkDeleteOrders)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: BulkIdParams) => bulkDeleteOrders(params),
    onSuccess: async response => {
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      handleBulkDeleteSuccess(response)
    }
  })
}

export const useCreateGuestOrder = () => {
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: ({ params, currency }: { params: AdminCreateGuestOrderParams; currency?: string }) =>
      createGuestOrder(params, currency),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(t('created_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_creating'))
    }
  })
}

export const useCreateRegisteredOrder = () => {
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: ({ params, currency }: { params: AdminCreateRegisteredOrderParams; currency?: string }) =>
      createRegisteredOrder(params, currency),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(t('created_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_creating'))
    }
  })
}

export const useAddOrderItem = (orderId: string) => {
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: (params: AddOrderItemParams) => addOrderItem(orderId, params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      toast.success(t('updated_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_updating'))
    }
  })
}

export const useUpdateOrderItem = (orderId: string) => {
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: ({ itemId, params }: { itemId: string; params: UpdateOrderItemParams }) =>
      updateOrderItem(orderId, itemId, params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      toast.success(t('updated_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_updating'))
    }
  })
}

export const useRemoveOrderItem = (orderId: string) => {
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: (itemId: string) => removeOrderItem(orderId, itemId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      toast.success(t('deleted_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_deleting'))
    }
  })
}

export const useAddOrderNote = (orderId: string) => {
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: (params: OrderNoteParams) => addOrderNote(orderId, params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      toast.success(t('created_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_creating'))
    }
  })
}

export const useUpdateOrderNote = (orderId: string) => {
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: ({ noteId, params }: { noteId: string; params: OrderNoteParams }) =>
      updateOrderNote(orderId, noteId, params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      toast.success(t('updated_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_updating'))
    }
  })
}

export const useDeleteOrderNote = (orderId: string) => {
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationFn: (noteId: string) => deleteOrderNote(orderId, noteId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      toast.success(t('deleted_successfully'))
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_deleting'))
    }
  })
}

export const useDownloadOrderExport = () => {
  const t = useTranslations()

  return useMutation({
    mutationFn: ({ id, type, locale }: { id: string; type: 'excel' | 'pdf'; locale: string }) =>
      downloadOrderExport(id, type, locale),
    onSuccess: ({ blob, filename }, { id, type }) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = filename || `order-${id}.${type === 'excel' ? 'xlsx' : 'pdf'}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_downloading_file'))
    }
  })
}
